import React, { useEffect } from 'react';
import { NotFound } from '@shootsta/common-react';
import { captureMessage, addBreadcrumb } from '../../config/sentry';

type NotFoundWithSentryProps = {
  heading?: string;
  subtitle?: string;
  homeUrl?: string;
  [key: string]: unknown; // Allow any additional props to be passed through
};

/**
 * Enhanced NotFound component that logs 404 events to Sentry in production
 * This wrapper maintains the same API as the original NotFound component
 * while adding Sentry logging for investigation purposes
 */
const NotFoundWithSentry: React.FC<NotFoundWithSentryProps> = (props) => {
  useEffect(() => {
    // Log the 404 event to Sentry
    logNotFoundToSentry();
  }, []);

  /**
   * Log 404 event to Sentry with useful context
   */
  const logNotFoundToSentry = () => {
    try {
      // Add breadcrumb for navigation tracking
      addBreadcrumb('404 Page Not Found', 'navigation', 'warning', {
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        referrer: document.referrer,
        timestamp: new Date().toISOString()
      });

      // Capture the 404 event as a message (not an error)
      // Context is handled centrally, only add 404-specific context
      const notFoundContext = {
        notFound: {
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
          referrer: document.referrer,
          timestamp: new Date().toISOString(),
          pathSegments: window.location.pathname.split('/').filter(Boolean),
          sessionStart: sessionStorage.getItem('sessionStart') || 'unknown'
        }
      };

      captureMessage(
        `404 Page Not Found: ${window.location.pathname}`,
        'warning',
        notFoundContext
      );
    } catch (error) {
      console.error('Failed to log 404 to Sentry:', error);
    }
  };

  // Render the original NotFound component with all props passed through
  return <NotFound {...props} />;
};

export default NotFoundWithSentry;
